// ============================================================================
// stage1-image-text-triage.ts: which imported images carry writing?
// ----------------------------------------------------------------------------
// Peter, 2026-10-13: "Make sure to process jpg and image files that may be the
// RFQ. Currently it doesn't."
//
// Every image is sorted by its CONTENT, never its file name (Peter,
// 2026-10-13: file names must not decide anything, only the contents). ONE
// call per up to 12 images, each sent as a small JPEG (long side 512px), with
// no file name in the request, into:
//   document  a page, sheet or screen of writing;
//   both      a site photo that also shows meaningful writing;
//   photo     a site photo with no meaningful writing.
// Anything that cannot be parsed counts as "photo", so a bad answer can only
// cost a missed document, never a lost photo. OCR is only paid for images
// sorted as document or both.
//
// The model comes from the registry task 'image-text-triage' (same lane as
// 'image-ocr'). No timeout and no AbortController on the AI call.
// ============================================================================

import { callOpenRouterVision, getMaxOutputTokens } from './ai-api';
import { pickModelForTask } from './model-selector';
import { getPrompt } from '../config/pipeline-registry';
import { IMAGE_TEXT_TRIAGE_PROMPT } from '../config/prompts';
import { renderImageToJpeg, type ImageSource } from './image-text-reading';

export type ImageTextKind = 'document' | 'photo' | 'both';

export const IMAGE_TEXT_TRIAGE_TASK = 'image-text-triage';
export const IMAGE_TEXT_TRIAGE_BATCH_SIZE = 12;
export const IMAGE_TEXT_TRIAGE_MAX_SIDE = 512;
const TRIAGE_JPEG_QUALITY = 0.7;

/**
 * HEIC/HEIF: most desktop browsers cannot decode these. Used ONLY to word the
 * import notice for an image that already failed to decode; it routes nothing.
 */
export function isHeicLike(file: { name?: unknown; type?: unknown } | null | undefined): boolean {
    return /\.(heic|heif)$/i.test(String(file?.name || ''))
        || /^image\/hei[cf]/i.test(String(file?.type || ''));
}

/** Split into batches of at most `size` (12 per triage call). */
export function chunkForTriage<T>(items: readonly T[], size: number = IMAGE_TEXT_TRIAGE_BATCH_SIZE): T[][] {
    const step = Math.max(1, Math.floor(size) || IMAGE_TEXT_TRIAGE_BATCH_SIZE);
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += step) out.push(items.slice(i, i + step));
    return out;
}

/**
 * Parse the triage answer for `count` images. Strict JSON (a ```json fence
 * around it is tolerated). Every image the answer does not clearly name as
 * "document" or "both" is a photo.
 */
export function parseImageTextTriage(raw: unknown, count: number): ImageTextKind[] {
    const kinds: ImageTextKind[] = Array.from({ length: Math.max(0, count) }, () => 'photo');
    let parsed: any;
    try {
        const text = String(raw ?? '')
            .trim()
            .replace(/^```(?:json)?\s*/i, '')
            .replace(/\s*```$/, '');
        parsed = JSON.parse(text);
    } catch {
        return kinds;
    }
    const rows = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.images) ? parsed.images : null);
    if (!rows) return kinds;
    const seen = new Set<number>();
    for (const row of rows) {
        const index = Number(row?.index);
        if (!Number.isInteger(index) || index < 1 || index > count || seen.has(index)) continue;
        seen.add(index);
        const kind = String(row?.kind || '').trim().toLowerCase();
        if (kind === 'document' || kind === 'both' || kind === 'photo') kinds[index - 1] = kind;
    }
    return kinds;
}

/** A 512px JPEG data URL for the triage call, or null when the image cannot be decoded. */
export async function makeTriageThumbnail(source: ImageSource): Promise<string | null> {
    const rendered = await renderImageToJpeg(source, IMAGE_TEXT_TRIAGE_MAX_SIDE, TRIAGE_JPEG_QUALITY);
    return rendered ? rendered.dataUrl : null;
}

/**
 * One triage call for up to 12 thumbnails. Throws when the call fails (the
 * caller then keeps those images as photos); an unreadable answer returns
 * "photo" for every image.
 */
export async function triageImageBatch({
    thumbnails,
    apiKey,
    model = '',
    projectId = null,
}: {
    thumbnails: string[];
    apiKey: string;
    model?: string | null;
    projectId?: string | null;
}): Promise<ImageTextKind[]> {
    const count = thumbnails.length;
    if (count === 0) return [];
    if (count > IMAGE_TEXT_TRIAGE_BATCH_SIZE) {
        throw new RangeError(`Image text triage takes at most ${IMAGE_TEXT_TRIAGE_BATCH_SIZE} images per call (got ${count}).`);
    }
    const triageModel = pickModelForTask(IMAGE_TEXT_TRIAGE_TASK, model || '');
    const prompt = (getPrompt(IMAGE_TEXT_TRIAGE_TASK) || IMAGE_TEXT_TRIAGE_PROMPT)
        .split('{IMAGE_COUNT}').join(String(count));
    const raw = await callOpenRouterVision({
        apiKey,
        model: triageModel,
        prompt,
        images: thumbnails,
        temperature: 0,
        maxTokens: getMaxOutputTokens(triageModel),
        pipelineId: IMAGE_TEXT_TRIAGE_TASK,
        stageName: '1',
        signal: undefined,
        task: IMAGE_TEXT_TRIAGE_TASK,
        // No client transport timer on a Stage 1 vision call.
        timeoutMs: 0,
        responseFormat: { type: 'json_object' },
        reasoningEffort: undefined,
        projectId: projectId || undefined,
        logicalCallId: undefined,
        supportOperation: undefined,
    });
    return parseImageTextTriage(raw, count);
}
